/**
 * ModuleBreadcrumb (P32.1).
 *
 * Tiny chrome element rendered between the AppShell header and the
 * current route's <Outlet />. Reads useLocation() and surfaces:
 *
 *   Operational Twin > Cascade
 *
 * The module name (left) links back to the module home; the page
 * label (right) is plain text. Home (/) renders nothing.
 *
 * Why this exists: v2's 3-module architecture is right but the
 * SPA navigation is flat below module level -- you click "Cascade"
 * from the Twin home, land at /twin/cascade, and lose any sense of
 * where you are. The breadcrumb gives the operator a reliable
 * trail without forcing a persistent sidebar (which v2 explicitly
 * rejects -- see docs/v2-platform-plan.md § 7).
 *
 * Why not generic path-walking: we hand-pick the page labels so
 * the breadcrumb reads in English ("Cascade", not "cascade") and
 * dynamic segments (UUIDs, framework codes) collapse to their
 * parent's label rather than "/twin/ibs/3aab2... > 3aab2...".
 *
 * Extension: when a new route ships, add its key+label to
 * PAGE_LABELS below. Unknown routes render just the module name.
 */
import { useMe } from "shared/api/useMe";
import { useLicenseStatus } from "shared/licensing/useLicense";
import { QUESTIONS, questionFor, visibleDestinations } from "shared/layout/questions";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * The modules that get a breadcrumb, and the catalogue key for each.
 *
 * These were hard-coded English strings. The eslint rule that keeps
 * copy in the catalogue reads literals in JSX CHILDREN, and a record of
 * labels is data -- so the breadcrumb on every page of the product was
 * never translatable, and two i18n sweeps passed over it.
 */
const MODULE_KEYS: Record<string, string> = {
  foundation: "nav.crumb.module.foundation",
  twin: "nav.crumb.module.twin",
  framework: "nav.crumb.module.framework",
  board: "nav.crumb.module.board",
  admin: "nav.crumb.module.admin",
};

/**
 * Page labels keyed by the FIRST TWO path segments joined ("twin/cascade").
 * For 3+ segment routes (e.g. /twin/ibs/:id), we walk DOWN to fewer
 * segments until we hit a match -- so /twin/ibs/abc-123 falls back
 * to whatever "twin/ibs" maps to (here it's "Entity detail").
 *
 * Routes with no PAGE_LABELS entry render just the module name +
 * arrow (no leaf label). Keeps the breadcrumb honest on routes we
 * haven't curated.
 */
const PAGE_KEYS: Record<string, string> = {
  "foundation/admin": "nav.crumb.page.foundation__admin",
  "foundation/admin/ibses": "nav.crumb.page.foundation__admin__ibses",
  "foundation/admin/services": "nav.crumb.page.foundation__admin__services",
  "foundation/admin/capabilities": "nav.crumb.page.foundation__admin__capabilities",
  "foundation/admin/vendors": "nav.crumb.page.foundation__admin__vendors",
  "foundation/admin/products": "nav.crumb.page.foundation__admin__products",
  "foundation/admin/configuration-items":
    "nav.crumb.page.foundation__admin__configuration-items",
  "foundation/admin/channels": "nav.crumb.page.foundation__admin__channels",
  "foundation/admin/journeys": "nav.crumb.page.foundation__admin__journeys",
  "foundation/mvb": "nav.crumb.page.foundation__mvb",
  "twin/cascade": "nav.crumb.page.twin__cascade",
  "twin/channels": "nav.crumb.page.twin__channels",
  "twin/journeys": "nav.crumb.page.twin__journeys",
  "twin/scenarios": "nav.crumb.page.twin__scenarios",
  "twin/drills": "nav.crumb.page.twin__drills",
  "twin/incident-reports": "nav.crumb.page.twin__incident-reports",
  "twin/war-room": "nav.crumb.page.twin__war-room",
  "twin/fragility": "nav.crumb.page.twin__fragility",
  "twin/telemetry": "nav.crumb.page.twin__telemetry",
  "twin/capability-map": "nav.crumb.page.twin__capability-map",
  "twin/landscape": "nav.crumb.page.twin__landscape",
  "twin/capmap": "nav.crumb.page.twin__capmap",
  "twin/constellation": "nav.crumb.page.twin__constellation",
  "twin/ibs": "nav.crumb.page.twin__ibs",
  "twin/mvb-candidates": "nav.crumb.page.twin__mvb-candidates",
  "twin/field-exam": "nav.crumb.page.twin__field-exam",
  "twin/service": "nav.crumb.page.twin__service",
  "twin/capability": "nav.crumb.page.twin__capability",
  "twin/vendor": "nav.crumb.page.twin__vendor",
  "twin/ci": "nav.crumb.page.twin__ci",
  "framework/assessments": "nav.crumb.page.framework__assessments",
  "framework/attestations": "nav.crumb.page.framework__attestations",
  "framework/regulatory-change": "nav.crumb.page.framework__regulatory-change",
  "framework/letters": "nav.crumb.page.framework__letters",
  "framework/letters/new": "nav.crumb.page.framework__letters__new",
  "framework/queue": "nav.crumb.page.framework__queue",
  "framework/caps": "nav.crumb.page.framework__caps",
  "admin/users": "nav.crumb.page.admin__users",
  "admin/audit-events": "nav.crumb.page.admin__audit-events",
  "admin/connectors": "nav.crumb.page.admin__connectors",
  "admin/settings": "nav.crumb.page.admin__settings",
  "admin/setup": "nav.crumb.page.admin__setup",
};

function pickPageLabel(segments: string[]): string | null {
  // Try longest-prefix-first so deeper-curated routes (e.g.
  // framework/letters/new) win over their parent's label.
  for (let n = Math.min(segments.length, 3); n >= 2; n--) {
    const key = segments.slice(0, n).join("/");
    if (PAGE_KEYS[key]) return PAGE_KEYS[key];
  }
  return null;
}

export function ModuleBreadcrumb() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const me = useMe();
  const lic = useLicenseStatus();
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null; // home page renders no breadcrumb
  const moduleKey = segments[0];
  if (!moduleKey) return null;
  const moduleLabelKey = MODULE_KEYS[moduleKey];
  if (!moduleLabelKey) return null; // unknown module = no breadcrumb (defensive)
  // Navigation re-cut, option A: a page a question claims leads with the
  // question it answers, not the module that holds it.
  const question = QUESTIONS.find((q) => q.key === questionFor(pathname));
  // Round 18 Q4 -- and is named as that question names it: the group
  // editor read "What matters most here? › Admin", the module's
  // administration fallback, where the question lists it by name.
  const destination = question?.destinations
    .filter((d) => pathname === d.to || pathname.startsWith(d.to + "/"))
    .sort((a, b) => b.to.length - a.to.length)[0];
  const curated = pickPageLabel(segments);
  const pageLabelKey =
    destination && (curated === null || curated === "nav.crumb.page.foundation__admin")
      ? destination.labelKey
      : curated;
  /* Round 19 Q5 -- the crumb linked the question's first destination
     whatever the seat, and the service owner landed on a page that
     refused him. It links the first one this seat may open, and is text
     where the seat may open none. */
  // Before the seat is known, the question's own first page, as before.
  const openable = question
    ? me.data
      ? visibleDestinations(question, me.data, lic.data)
      : question.destinations
    : [];
  const head = question
    ? { to: openable[0]?.to ?? null, label: t(question.questionKey) }
    : { to: `/${moduleKey}`, label: t(moduleLabelKey) };
  return (
    <nav
      data-testid="module-breadcrumb"
      data-module={moduleKey}
      data-question={question?.key}
      aria-label="Breadcrumb"
      className="mb-4 text-xs text-slate-500"
    >
      {head.to ? (
        <Link
          to={head.to}
          data-testid="module-breadcrumb-module-link"
          className="hover:text-slate-700 underline underline-offset-2"
        >
          {head.label}
        </Link>
      ) : (
        <span data-testid="module-breadcrumb-module-text">{head.label}</span>
      )}
      {pageLabelKey && (
        <>
          <span className="mx-2" aria-hidden>
            ›
          </span>
          <span data-testid="module-breadcrumb-page" className="text-slate-700">
            {t(pageLabelKey)}
          </span>
        </>
      )}
    </nav>
  );
}
